<template>
  <div class="inline-flex min-w-px items-center justify-start gap-2">
    <RfFilter
      key="filters"
      hideIcon
      activator="Filters"
      :groups="{ filters: { el: possibleFilters, key: 'key', value: 'value' } }"
      :selected="{ filters }"
      @selected="items => ((filters = items.filters), updateFiltersData(items))"
    />
    <TransitionGroup
      mode="out-in"
      class="inline-flex items-center justify-start gap-2 overflow-auto"
      tag="div"
    >
      <RfSelect
        v-if="filters.language"
        key="language"
        extended
        apply
        multiple
        customValues
        returnObject
        autoWidth
        variant="outlined"
        itemKey="key"
        itemValue="value"
        :search="`Search ${filters.language}s`"
        :activator="filters.language"
        :items="languageEntries"
        :modelValue="selectedFilterItems('language')"
        @selected="items => updateFilterSelection('language', items)"
      />
      <RfFilter
        v-if="filters.segment?.length > 1"
        key="segment"
        extended
        :isLoading="segmentFilterIsLoading"
        :search="`Search ${filters.segment}s`"
        :activator="filters.segment"
        :groups="{ segment: { el: props.segments, key: 'id', value: 'name' } }"
        :selected="filtersData"
        @selected="items => (filtersData = { ...filtersData, ...items })"
      />
      <RfSelect
        v-if="filters.deviceCategory"
        key="deviceCategory"
        extended
        apply
        multiple
        customValues
        returnObject
        autoWidth
        variant="outlined"
        itemKey="key"
        itemValue="value"
        :search="'Search Device Categories'"
        :activator="filters.deviceCategory"
        :items="deviceCategoryEntries"
        :modelValue="selectedFilterItems('deviceCategory')"
        @selected="items => updateFilterSelection('deviceCategory', items)"
      />
      <RfFilter
        v-if="filters.deviceType"
        key="deviceType"
        extended
        :search="`Search ${filters.deviceType}s`"
        :activator="filters.deviceType"
        :groups="{ deviceType: { el: deviceTypeEntries, key: 'key', value: 'value' } }"
        :selected="filtersData"
        @selected="items => (filtersData = { ...filtersData, ...items })"
      />
    </TransitionGroup>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfFilter from "@/components/inputs/RfFilter.vue";
import StringUtils from "@/utils/StringUtils";

const props = defineProps({
  segments: { type: Array, default: () => [] },
  appDevices: { type: Array, default: () => [] },
  appLanguages: { type: Array, default: () => [] },
  appDeviceTypes: { type: Array, default: () => [] },
  segmentFilterIsLoading: { type: Boolean, default: false },
});

const filters = ref({});
const filtersData = defineModel("filtersData", {
  default: () => ({
    language: {},
    segment: {},
    deviceCategory: {},
    deviceType: {},
  }),
  type: Object,
});

const defaultDeviceCategories = [
  { key: "desktop", value: "Desktop" },
  { key: "mobile", value: "Mobile" },
  { key: "smarttv", value: "Smart TV" },
  { key: "tv", value: "TV" },
  { key: "tablet", value: "Tablet" },
  { key: "phone", value: "Phone" },
];

const possibleFilters = computed(() => [
  ...(props.segments.length > 1 ? [{ key: "segment", value: "Segment" }] : []),
  { key: "language", value: "Language" },
  { key: "deviceCategory", value: "Device Category" },
  ...(props.appDeviceTypes?.length > 0 ? [{ key: "deviceType", value: "Device Type" }] : []),
]);

const updateFiltersData = ({ filters }) => {
  const newFilters = {};
  const keys = Object.keys(filtersData.value).filter(key => !Object.hasOwn(filters, key));
  keys.forEach(key => (newFilters[key] = {}));
  filtersData.value = { ...filtersData.value, ...newFilters };
};

// language/deviceCategory use RfSelect's flat + customValues mode, which works with
// {key, value} item objects rather than filtersData's {[key]: value} map — convert both ways.
const selectedFilterItems = filterKey =>
  Object.entries(filtersData.value[filterKey] || {}).map(([key, value]) => ({ key, value }));

const updateFilterSelection = (filterKey, items) => {
  filtersData.value = {
    ...filtersData.value,
    [filterKey]: Object.fromEntries(items.map(i => [i.key, i.value])),
  };
};

const deviceCategoryEntries = computed(() => {
  if (props.appDevices?.length > 0)
    return props.appDevices.map(d => ({ key: d, value: StringUtils.capitalize(d) }));
  return defaultDeviceCategories;
});

const deviceTypeEntries = computed(() =>
  (props.appDeviceTypes ?? []).map(d =>
    typeof d === "string" ? { key: d, value: StringUtils.capitalize(d) } : d,
  ),
);

const languageEntries = computed(() => {
  if (props.appLanguages?.length > 0) return props.appLanguages.map(l => ({ key: l, value: l }));
  return [];
});
</script>
